import { InvestmentRoundingSetting } from 'shared';
import { z } from 'zod';

import { getTranslation } from '../../../utils/get-translation.util';

export const MAX_FILE_SIZE = 1024 * 1024 * 10;

export const ACCEPTED_FILE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg',
];
export const fileSchemaMessage = getTranslation('FILE_SIZE_TOO_LARGE');

export const subFilesSchema = z.array(
  z.object({
    docLink: z.string(),
    loadProgress: z.number().max(100),
    abort: z.function(),
    id: z.string(),
    doc: z
      .custom<File>()
      .or(
        z.object({
          size: z.number(),
          type: z.string(),
          name: z.string(),
        }),
      )
      .refine(
        ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
        getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
      )
      .refine(({ size }) => size <= MAX_FILE_SIZE, fileSchemaMessage),
  }),
);

export const stepOne = z.object({
  roundingSetting: z.enum(InvestmentRoundingSetting).default(InvestmentRoundingSetting.DEFAULT),
  conversionDate: z.coerce.date(),
  linkedEvent: z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: getTranslation('REQUIRED') }),
    id: z
      .string()
      .trim()
      .min(1, { message: getTranslation('REQUIRED') }),
    sharePrice: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
    issuedSharesOnStart: z.coerce.number({
      message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
    }),
    issuedShares: z.coerce.number({
      message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
    }),
  }),
  shareClass: z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: getTranslation('REQUIRED') }),
    id: z
      .string()
      .trim()
      .min(1, { message: getTranslation('REQUIRED') }),
  }),
  safeIds: z
    .array(z.string().min(1, { message: getTranslation('REQUIRED') }))
    .min(1, { message: getTranslation('REQUIRED') }),
  safes: z
    .array(
      z.object({
        id: z
          .string()
          .trim()
          .min(1, { message: getTranslation('REQUIRED') }),
        stakeholder: z.object({
          fullName: z
            .string()
            .trim()
            .min(1, { message: getTranslation('REQUIRED') }),
          id: z
            .string()
            .trim()
            .min(1, { message: getTranslation('REQUIRED') }),
        }),
        investment: z.coerce
          .number()
          .min(1, { message: getTranslation('INVESTMENT_AMOUNT_REQUIRED') }),
        issueDate: z.coerce.date(),

        floorValue: z.coerce.number({
          message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
        }),
        files: subFilesSchema.optional(),
      }),
    )
    .min(1, { message: getTranslation('REQUIRED') }),
  conversionSummary: z
    .object({
      sharePrice: z.coerce.number({
        message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
      }),
      investment: z.coerce.number({
        message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
      }),
      preMoneyValuation: z.coerce.number({
        message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
      }),
      totalOwned: z.coerce.number({
        message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
      }),
      convertedShares: z.coerce.number({
        message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE'),
      }),
    })
    .optional(),
  conversionDetails: z
    .array(
      z.object({
        id: z.string(),
        summary: z.record(z.string(), z.unknown()),
      }),
    )
    .optional(),
});

export const stepThree = z
  .object({
    additionalNotes: z
      .string()
      .max(3000, getTranslation('MAXIMUM_CHARACTERS', { count: 3000 }))
      .optional(),
    files: z
      .object({
        docLink: z.string(),
        loadProgress: z.number().max(100),
        abort: z.function(),
        id: z.string(),
        doc: z
          .custom<File>()
          .or(
            z.object({
              size: z.number(),
              type: z.string(),
              name: z.string(),
            }),
          )
          .refine(
            ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
            getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
          )
          .refine(({ size }) => size <= MAX_FILE_SIZE, fileSchemaMessage),
      })
      .array()

      .optional(),
  })
  .optional();

export const formSchema = z.object({
  stepOne,
  stepThree,
});

export type FormSchema = z.infer<typeof formSchema>;
